import { Button } from '@/components/Button'
import { ButtonLink } from '@/components/ButtonLink'
import { Icon } from '@/icons'
import { apiService } from '@/services/Api'
import { getMailProviderLink } from '@/utils/emails'
import { formatTimerValue } from '@/utils/number'
import cn from 'classnames'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './EmailVerified.module.scss'

interface EmailVerifiedProps {
  className?: string
  email: string
  isSuccessRegistration?: boolean
}
export const EmailVerified: FC<EmailVerifiedProps> = ({
  className,
  email,
  isSuccessRegistration = false,
}) => {
  const isInitRef = useRef(false)
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)

  const resendEmail = useCallback(() => {
    return apiService.user
      .resendVerificationEmail({
        resendVerificationEmailRequest: {
          email,
        },
      })
      .then((response) => {
        if (response.status === true) {
          setSecondsLeft(response.data.nextAttemptIn)
        }
      })
  }, [email])

  useEffect(() => {
    if (secondsLeft == null || secondsLeft <= 0) {
      return
    }

    const timer = setTimeout(() => {
      setSecondsLeft((prev) => (prev != null ? prev - 1 : null))
    }, 1000)

    return () => clearTimeout(timer)
  }, [secondsLeft])

  // Первый запрос при монтировании; ref-гард защищает от повторного вызова
  // (в dev StrictMode эффект отрабатывает дважды)
  useEffect(() => {
    if (isInitRef.current) {
      return
    }

    isInitRef.current = true
    resendEmail()
  }, [resendEmail])

  const isTimerActive = secondsLeft != null && secondsLeft > 0

  const mailProviderLink = useMemo(() => {
    return getMailProviderLink(email)
  }, [email])

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.content}>
        <div className={styles.header}>
          {isSuccessRegistration ? (
            <div className={styles.iconTitleWrapRegistration}>
              <Icon
                name="checkCircle2"
                className={styles.iconTitleRegistration}
              />
            </div>
          ) : (
            <div className={styles.iconTitleWrap}>
              <Icon name="attention" className={styles.iconTitle} />
            </div>
          )}
          <div className={styles.title}>
            {isSuccessRegistration ? (
              <>
                Поздравляем!
                <br /> Вы успешно зарегистрированы
              </>
            ) : (
              'Необходимо подтвердить почту'
            )}
          </div>
        </div>
        <div className={styles.description}>
          Мы отправили на вашу почту{' '}
          <span className={styles.emailText}>{email}</span> ссылку для
          подтверждения регистрации. После перехода по ссылке вы сможете начать
          работу с личным кабинетом.
        </div>
      </div>
      <div className={styles.buttons}>
        {mailProviderLink != null ? (
          <ButtonLink href={mailProviderLink} target="_blank">
            <span className={styles.buttonText}>Перейти в почтовый ящик</span>
          </ButtonLink>
        ) : null}
        <Button onClick={resendEmail} disabled={isTimerActive}>
          <span className={styles.buttonText}>
            {isTimerActive && secondsLeft != null
              ? `Отправить письмо повторно через ${formatTimerValue(secondsLeft)}`
              : 'Отправить письмо повторно'}
          </span>
        </Button>
      </div>
    </div>
  )
}
